import { defineStore } from 'pinia'
import { api } from '@/libs/mande'
import { onBeforeUnmount, ref, nextTick, onMounted, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import type { Fn } from '@vueuse/core'
import { useAuthStore } from '@/stores/auth'

interface MessageParams {
  id?: number
  text: string
  author_id: number
  sent_at?: Date
  read?: boolean
  clinician_id: number
  client_id: number
}

export class Message {
  id: number
  text: string
  sent_at: Date
  read: boolean
  author_id: number
  clinician_id: number
  client_id: number

  constructor({ id, text, author_id, sent_at, read, clinician_id, client_id }: MessageParams) {
    this.id = id !== undefined ? id : new Date().getTime()
    this.text = text
    this.sent_at = sent_at !== undefined ? sent_at : new Date()
    this.read = read !== undefined ? read : false
    this.author_id = author_id
    this.clinician_id = clinician_id
    this.client_id = client_id
  }
}

interface PostMessageResponse {
  message: Message
}

interface GetMessagesResponse {
  messages: Message[]
}

export interface MessagesStream {
  close: () => void
}

export const useMessagesStore = defineStore('messages', () => {
  // TODO: refactor this to use a single messages map

  // this map is for managing messages between the clinician and multiple clients when the clinician is logged in
  const clinicianMessages = ref(new Map<Number, Message[]>()) // key is user id
  // this list is for managing messages between the user and the clinician when the user is logged in
  const messages = ref([] as Message[])
  const unreadMessages = ref([] as Message[])
  // let eventsource: EventSource

  onMounted(async () => {
    // get current user from user store
    // const userStore = useUserStore()
    // const currentUser = await userStore.getCurrentUser()
    // if (currentUser?.role === 'user') {
    //   eventsource = new EventSource('/api/messages/stream')
    //   eventsource.onmessage = async (event) => {
    //     const msg = JSON.parse(event.data)
    //     const m = new Message(msg)
    //     // only add the message if we don't already have it
    //     if (!messages.value.find((m: Message) => m.id === msg.id)) {
    //       messages.value.push(m)
    //     }
    //     await nextTick()
    //     const chatWindow = document.querySelector('.conversation')
    //     if (chatWindow !== null) chatWindow.scrollTo(0, chatWindow.scrollHeight + 1000)
    //   }
    // }
    //add messages to unreadText list from clinicianMessages Map
    // for (let value of clinicianMessages.value.values()) {
    //   for (let message of value) {
    //     if (message.read === false) {
    //       unreadMessages.value.push(message)
    //     }
    //   }
    // }
    // console.log('Unread messages:', unreadMessages.value)
    // console.log('count: ' + unreadMessages.value.length)
  })
  onBeforeUnmount(() => {
    // eventsource?.close()
  })

  // const updateUnreadMessages = () => {
  //   //new method to update the unread messages
  //   unreadMessages.value = []
  //   for (let value of clinicianMessages.value.values()) {
  //     for (let message of value) {
  //       if (message.read === false && message.author_id == message.client_id) {
  //         unreadMessages.value.push(message)
  //       }
  //     }
  //   }
  //   console.log('Unread messages:', unreadMessages.value)
  //   // console.log('count: ' + unreadMessages.value.length)
  // }

  const addToClinicianMessages = (message: Message) => {
    if (!clinicianMessages.value.has(message.client_id)) {
      clinicianMessages.value.set(message.client_id, [])
    }
    const clientMessages = clinicianMessages.value.get(message.client_id)!

    if (!clientMessages.find((existing) => existing.id === message.id)) {
      clientMessages.push(message)
    }
  }

  const updateUnreadMessages = () => {
    unreadMessages.value = []
    for (const messagesList of clinicianMessages.value.values()) {
      const unread = messagesList.filter(
        (message) => !message.read && message.author_id === message.client_id
      )
      unreadMessages.value.push(...unread)
    }
  }

  const getMessages = async (): Promise<Message[]> => {
    const { messages: newMessages } = await api.get<GetMessagesResponse>('/messages')
    if (newMessages)
      newMessages.forEach((msg: Message) => {
        if (!messages.value.find((m: Message) => m.id === msg.id)) {
          const m = new Message(msg)
          messages.value.push(m)
        }
      })
    return newMessages
  }

  const getMessagesPolling = async (
    onMessage: (message: Message) => void
  ): Promise<MessagesStream> => {
    let since = 0
    let stop = false
    let lastMessageTime = new Date()
    const result = {
      close: () => {
        stop = true
      }
    } as MessagesStream

    const f = async (onMessage: (message: Message) => void) => {
      for (;;) {
        if (stop) {
          break
        }
        const url = `/messages/longpoll?since=${since}`
        try {
          const { messages: msgs } = await api.get<GetMessagesResponse>(url)
          msgs?.forEach((msg: Message) => {
            since = msg.id
            onMessage(msg)
            lastMessageTime = new Date()
          })
        } catch (e) {
          // todo: update the ui with some indication that messages aren't loading?
          // useAuthStore().handleAPIError(e)
          stop = true
        }
        if (!stop) await sleepForABit(lastMessageTime)
      }
    }

    f(onMessage) // async

    return result
  }

  const getClinicianMessagesForUser = async (userId: Number) => {
    const { messages: newMessages } = await api.get<GetMessagesResponse>(
      '/clinician/messages?user_id=' + userId
    )
    if (newMessages)
      newMessages.forEach((msg: Message) => {
        if (!messages.value.find((m: Message) => m.id === msg.id)) {
          const m = new Message(msg)
          addToClinicianMessages(m)
        }
      })
  }

  const getClinicianMessagesPollingGlobal = async (
    onMessage: (message: Message) => void
  ): Promise<MessagesStream> => {
    let since = 0
    let stop = false
    let lastMessageTime = new Date()
    const result = {
      close: () => {
        stop = true
      }
    } as MessagesStream

    const f = async (onMessage: (message: Message) => void) => {
      for (;;) {
        if (stop) {
          break
        }
        const url = `/clinician/messages/for_users/longpoll?since=${since}`
        try {
          const { messages: msgs } = await api.get<GetMessagesResponse>(url)
          msgs?.forEach((msg: Message) => {
            since = msg.id
            const m = new Message(msg)
            addToClinicianMessages(m)
            updateUnreadMessages()
            onMessage(m)
            lastMessageTime = new Date()
          })
        } catch (e) {
          stop = true
        }
        if (!stop) await sleepForABit(lastMessageTime)
      }
    }

    f(onMessage) // async

    return result
  }

  const getClinicianMessagesPollingPrivate = async (
    recipientId: Number,
    onMessage: (message: Message) => void
  ): Promise<MessagesStream> => {
    let since = 0
    let stop = false
    let lastMessageTime = new Date()
    const result = {
      close: () => {
        stop = true
      }
    } as MessagesStream

    const f = async (recipientId: Number, onMessage: (message: Message) => void) => {
      for (;;) {
        if (stop) {
          break
        }
        const url = `/clinician/messages/for_user/${recipientId}/longpoll?since=${since}`
        try {
          const { messages: msgs } = await api.get<GetMessagesResponse>(url)
          msgs?.forEach((msg: Message) => {
            since = msg.id
            onMessage(msg)
            lastMessageTime = new Date()
          })
        } catch (e) {
          stop = true
          // todo: update the ui with some indication that messages aren't loading?
          // useAuthStore().handleAPIError(e)
        }
        if (!stop) await sleepForABit(lastMessageTime)
      }
    }

    f(recipientId, onMessage) // async

    return result
  }

  const sleepForABit = async (lastChange: Date) => {
    const elapsedTime = new Date().getTime() - lastChange.getTime()
    let delay = 1000
    if (elapsedTime > 30000) {
      // 30 seconds
      delay = 10000 // 10 seconds
    }
    if (elapsedTime > 300000) {
      // 5 minutes
      delay = 30000 // 30 seconds
    }
    if (elapsedTime > 30 * 60 * 1000) {
      // 30 minutes
      delay = 5 * 60 * 1000 // 5 minutes
    }
    await new Promise((resolve) => setTimeout(resolve, delay))
  }

  const getClinicianMessagesStream = async (
    recipientId: Number,
    onMessage: (message: Message) => void
  ): Promise<MessagesStream> => {
    const url = '/api/clinician/messages/for_user/' + recipientId + '/stream'
    // eventsource = new EventSource(url)
    const result = {
      // close: () => eventsource.close()
    } as MessagesStream

    // eventsource.onmessage = async (event) => {
    //   const msg = JSON.parse(event.data)
    //   // check if event.data has key 'err'
    //   if (msg.err) {
    //     console.error(msg.err)
    //     return
    //   }
    //   console.log(event.data)
    //   const m = new Message(msg)
    //   // only add the message if we don't already have it
    //   if (!messages.value.find((m: Message) => m.id === msg.id)) {
    //     messages.value.push(m)
    //   }
    //   onMessage(m)
    // }

    return result
  }

  const addMessage = async (messageText: string): Promise<Message> => {
    const { message } = await api.post<PostMessageResponse>('/messages', {
      text: messageText
    })
    if (!messages.value.find((m: Message) => m.id === message.id)) {
      messages.value.push(message)
    }
    return message
  }

  const clinicianAddMessage = async (
    //new method to add message to clinicianMessages Map and messages list
    messageText: string,
    recipientId: Number
  ): Promise<Message> => {
    const { message } = await api.post<PostMessageResponse>('/clinician/messages', {
      text: messageText,
      reply_to_user_id: recipientId
    })
    if (!messages.value.find((m: Message) => m.id === message.id)) {
      messages.value.push(message)
    }
    return message
  }

  const markClinicianMessagesRead = async (
    author_id: Number,
    message_id: Number
  ): Promise<void> => {
    try {
      await api.put(`/messages/${message_id}/${author_id}/mark_read`)
    } catch (error) {
      console.error('Failed to mark messages as read:', error)
      throw error
    }
  }

  const markClientMessagesRead = async (author_id: Number, message_id: Number): Promise<void> => {
    try {
      await api.put(`/clinician/messages/${message_id}/${author_id}/mark_read`)
      const clientMessages = clinicianMessages.value.get(author_id) //this is the list of messages for the client
      if (clientMessages) {
        const message = clientMessages.filter((m) => m.read === false) //get the unread messages
        for (const m of message) {
          m.read = true
        }
      }
      updateUnreadMessages()
      // updateUnreadMessages()
      console.log('Unread messages:', unreadMessages.value)
    } catch (error) {
      console.error('Failed to mark messages as read:', error)
      throw error
    }
  }

  const resetMessagesState = () => {
    clinicianMessages.value.clear()
    messages.value = []
    unreadMessages.value = []
  }

  return {
    messages,
    clinicianMessages,
    getMessages,
    getMessagesPolling,
    addMessage,
    clinicianAddMessage,
    getClinicianMessagesForUser,
    getClinicianMessagesStream,
    getClinicianMessagesPollingGlobal,
    getClinicianMessagesPollingPrivate,
    unreadMessages,
    updateUnreadMessages,
    markClinicianMessagesRead,
    markClientMessagesRead,
    resetMessagesState
  }
})
